import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ApplicationFacade, Itinerary, SearchService, SessionService, ShoppingCartService } from '@ridango/components';
import Env from '@ridango/core/env';
import { takeUntil } from 'rxjs/operators';
import { AnonymousUserModalComponent } from '../../anonymous-user-modals/anonymous-user-modal/anonymous-user-modal.component';
import { DeviceIdService } from '@ridango/core/lib/services';
import { Subject } from 'rxjs';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { TicketPurchaseTimeModalData } from '@ridango/core';
import { JourneyTicketPurchaseTimeModalComponent } from '../journey-ticket-purchase-time-modal/journey-ticket-purchase-time-modal.component';
import { ModalController } from '@ionic/angular';
import { Temporal } from '@js-temporal/polyfill';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'rwidget-journey-price',
    templateUrl: './journey-price.component.html'
})
export class JourneyPriceComponent implements OnInit, OnDestroy {
    @Input() itinerary: Itinerary;
    price: string;
    currency: string;
    isEnabled;
    assetsPath$ = this.applicationFacade.assetsPath$;
    private componentDestroyed$ = new Subject<void>();
    private readonly BUY_TICKET = 'Buy ticket';
    isContrast$ = this.applicationFacade.highContrast$;

    constructor(
        private applicationFacade: ApplicationFacade,
        private readonly searchService: SearchService,
        private sessionService: SessionService,
        private deviceIdService: DeviceIdService,
        private shoppingCartService: ShoppingCartService,
        @Inject('ENV') public env: Env,
        private analyticsService: AnalyticsService,
        private modalCtrl: ModalController,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {
        if (this.env.WMB) {
            this.price = this.itinerary?.generalizedCost?.toString();
            this.currency = this.env.WMB?.CURRENCY;
        } else {
            this.price = this.convertCentsToPrice();
            this.currency = this.itinerary?.fare?.fare?.regular?.currency?.currency;
        }
        this.isEnabled = this.env.IS_NEW_AFC || (this.price && this.currency);
    }

    convertCentsToPrice(): string {
        const cents = +this.itinerary?.fare?.fare?.regular?.cents;
        if (cents > 0) {
            const price = (cents / 100).toString().replace('.', ',');
            return price;
        }
        return null;
    }

    buyTickets() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.JOURNEY_PLANNER,
            name: this.BUY_TICKET
        });

        if (!this.itinerary) return;

        const urlParams = this.searchService.getParamsForUrl(this.itinerary);

        if (!urlParams) {
            return;
        }

        this.sessionService.setJourneyQueryParams(urlParams);

        this.deviceIdService
            .getIsUserExist()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((deviceUserExist) => {
                const routeParams: string[] = ['journey-planner', 'purchase'];
                this.shoppingCartService.startToBuyTicket(deviceUserExist, routeParams, AnonymousUserModalComponent);
            });
    }

    public openTicketPurchaseModal = async (): Promise<void> => {
        if (!this.env.JOURNEY_PLANNER?.SHOW_TICKET_PURCHASE_TIME_MODAL) {
            this.buyTickets();
            return;
        }

        const MAX_MINUTES_UNTIL_DEPARTURE = this.env.JOURNEY_PLANNER?.MAX_MINUTES_UNTIL_DEPARTURE;
        const MIN_MINUTES_BEFORE_DEPARTURE = this.env.JOURNEY_PLANNER?.MIN_MINUTES_BEFORE_DEPARTURE;
        const departureTimeString = this.getDepartureTime();

        if (!departureTimeString) {
            this.buyTickets();
            return;
        }

        try {
            const departureTime = Temporal.Instant.from(departureTimeString);
            const now = Temporal.Now.instant();

            const duration = now.until(departureTime);
            const minutesUntilDeparture = duration.total('minutes');

            let modalData: TicketPurchaseTimeModalData;

            if (minutesUntilDeparture < MIN_MINUTES_BEFORE_DEPARTURE) {
                modalData = {
                    type: 'warning',
                    message: this.translateService.instant('TICKET_PURCHASE_WARNING_DEPARTED')
                };
            } else if (minutesUntilDeparture > MAX_MINUTES_UNTIL_DEPARTURE) {
                const purchaseAllowedTime = departureTime.subtract({ minutes: MAX_MINUTES_UNTIL_DEPARTURE });
                const formattedTime = this.formatTimeForDisplay(purchaseAllowedTime);

                modalData = {
                    type: 'error',
                    message: this.translateService.instant('TICKET_PURCHASE_ERROR_TOO_EARLY', {
                        maxMinutes: MAX_MINUTES_UNTIL_DEPARTURE,
                        purchaseTime: formattedTime
                    })
                };
            } else {
                this.buyTickets();
                return;
            }

            await this.showTimeModal(modalData);
        } catch (error) {
            console.error('Error parsing departure time:', error);
            this.buyTickets();
        }
    };

    private async showTimeModal(modalData: TicketPurchaseTimeModalData): Promise<void> {
        const modal = await this.modalCtrl.create({
            component: JourneyTicketPurchaseTimeModalComponent,
            cssClass: 'journey-ticket-purchase-time-modal',
            componentProps: {
                data: modalData,
                itinerary: this.itinerary
            }
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();
        if (data?.action === 'buy') {
            this.buyTickets();
        }
    }

    private getDepartureTime(): string | null {
        if (!this.itinerary?.startTime) return null;

        const departureDate = new Date(this.itinerary.startTime);
        return departureDate.toISOString();
    }

    private formatTimeForDisplay(instant: Temporal.Instant): string {
        const zonedDateTime = instant.toZonedDateTimeISO(Temporal.Now.timeZoneId());
        const plainDateTime = zonedDateTime.toPlainDateTime();

        return `${plainDateTime.toPlainDate().toString()} ${plainDateTime.toPlainTime().toString().slice(0, 5)}`;
    }

    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }
}
